import { useCallback } from 'react';
import { Edge } from '@xyflow/react';

/**
 * Custom hook for handling keyboard events in React Flow dependency maps
 *
 * This hook manages keyboard interactions for:
 * 1. Deleting selected edges (Delete/Backspace keys)
 * 2. Escaping from drag-and-connect mode (Escape key)
 * 3. Deselecting edges (Escape key when not in drag mode)
 *
 * Key behaviors:
 * - Delete/Backspace: Removes all selected dependency edges
 * - Escape: Priority system - exits drag mode first, then deselects edges
 * - All events are properly prevented from bubbling
 *
 * @param selectedEdges - Array of currently selected React Flow edges
 * @param removeDependenciesByIds - Function to remove dependencies by edge IDs
 * @param setSelectedEdges - State setter to update selected edges
 * @param dragAndConnectModeResultUuid - UUID of result in drag-and-connect mode (null if not active)
 * @param handleExitDragAndConnectMode - Function to exit drag-and-connect mode
 * @returns Keyboard event handler function for React Flow container
 */
export function useKeyboardHandler(
  selectedEdges: Edge[],
  removeDependenciesByIds: (edgeIds: string[]) => void,
  setSelectedEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
  dragAndConnectModeResultUuid: string | null = null,
  handleExitDragAndConnectMode?: (resultUuid: string) => void,
) {
  return useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedEdges.length > 0) {
        // Remove selected edges from local state
        removeDependenciesByIds(selectedEdges.map((edge) => edge.id));
        setSelectedEdges([]);
      }

      // Handle Escape key
      if (event.key === 'Escape') {
        // Priority 1: Exit drag and connect mode if active
        if (dragAndConnectModeResultUuid && handleExitDragAndConnectMode) {
          handleExitDragAndConnectMode(dragAndConnectModeResultUuid);
        }
        // Priority 2: Deselect edges if any are selected (and not in drag mode)
        else if (selectedEdges.length > 0) {
          setSelectedEdges([]);
        }
      }
    },
    [
      selectedEdges,
      removeDependenciesByIds,
      setSelectedEdges,
      dragAndConnectModeResultUuid,
      handleExitDragAndConnectMode,
    ],
  );
}
